import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WorkflowStep } from "@/types/workflow";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface AIHelpDialogProps {
  step: WorkflowStep | null;
  open: boolean;
  onClose: () => void;
}

export function AIHelpDialog({ step, open, onClose }: AIHelpDialogProps) {
  const [aiResponse, setAiResponse] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const isCompanyIdentificationStep = step?.title.toLowerCase().includes('identifisere selskap');

  useEffect(() => {
    const fetchStoredHelp = async () => {
      if (!step?.case_id) return;
      
      const { data } = await supabase
        .from('workflow_step_ai_help')
        .select('ai_help_content')
        .eq('step_title', step.title)
        .eq('case_id', step.case_id)
        .maybeSingle();  // Changed from .single() to .maybeSingle()

      if (data) {
        setAiResponse(data.ai_help_content);
      }
    };

    if (open) {
      fetchStoredHelp();
    }
  }, [step, open]);

  useEffect(() => {
    const fetchAIHelp = async () => {
      if (!step || !open) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase.functions.invoke('workflow-ai-help', {
          body: {
            stepTitle: step.title,
            stepDescription: step.description,
            question: null,
            companyInfo: companyInfo || undefined
          }
        });

        if (error) throw error;
        
        // Store the AI help response
        if (step.case_id) {
          await supabase
            .from('workflow_step_ai_help')
            .upsert({
              case_id: step.case_id,
              step_title: step.title,
              ai_help_content: data.response,
              user_id: (await supabase.auth.getUser()).data.user?.id
            });

          // Show success message
          const alert = document.createElement('div');
          alert.innerHTML = `
            <div class="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md">
              <div class="flex items-center gap-3">
                <svg class="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <div>
                  <h3 class="font-semibold text-green-900">AI Hjelp lagret</h3>
                  <p class="text-green-700 text-sm">AI hjelpen er nå tilgjengelig i dette steget</p>
                </div>
              </div>
            </div>
          `;
          document.body.appendChild(alert);
          setTimeout(() => {
            document.body.removeChild(alert);
          }, 3000);
        }

        setAiResponse(data.response);
      } catch (error) {
        console.error('Error fetching AI help:', error);
        setError("Beklager, jeg kunne ikke generere et svar akkurat nå. Vennligst prøv igjen senere.");
        toast({
          variant: "destructive",
          title: "Feil ved generering av AI hjelp",
          description: "Kunne ikke generere AI hjelp. Vennligst prøv igjen senere."
        });
      } finally {
        setLoading(false);
      }
    };

    if (!aiResponse) {
      fetchAIHelp();
    }
  }, [step, open, companyInfo]);

  if (!step) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>AI-assistanse: {step.title}</DialogTitle>
        </DialogHeader>
        
        {isCompanyIdentificationStep && (
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Skriv inn organisasjonsnummer eller selskapsnavn"
              value={companyInfo}
              onChange={(e) => setCompanyInfo(e.target.value)}
            />
            <Button 
              variant="secondary"
              onClick={() => setCompanyInfo("")}
              disabled={!companyInfo}
            >
              Nullstill
            </Button>
          </div>
        )}

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Genererer AI-assistanse...</span>
            </div>
          ) : error ? (
            <div className="text-red-500 p-4 rounded-lg bg-red-50">
              {error}
            </div>
          ) : (
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap">{aiResponse}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}